import { useState } from 'react';

import { Commit, User } from 'src/components/types';
import urls from 'src/urls/commit';
import { publishUiEvent } from 'src/utils/analytics/publish';
import authRequest, { jsonHeaders } from 'src/utils/fetch';

import { UpdateCommitAction } from './commit-page-reducer';

export function useCommitApproveHooks(
  commit: Commit,
  currentUserKey: string,
  currentUser: User | null | undefined,
  dispatchCommitUpdate: (action: UpdateCommitAction) => void
): {
  approveCommit: () => void;
  unapproveCommit: () => void;
  isApproved: boolean;
  isApproveLoading: boolean;
} {
  const { hash } = commit;
  const fullSlug = commit.repository.full_name;

  const [isApproved, setApproved] = useState(
    commit.participants?.some(
      participant =>
        participant.approved && participant.user.uuid === currentUserKey
    ) || false
  );
  const [isApproveLoading, setApproveLoading] = useState(false);

  // Approves a commit, returns true if successful and false otherwise
  const approveCommit = async () => {
    const url = urls.api.v20.approve(fullSlug, hash);
    setApproveLoading(true);
    try {
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'approveButton',
        source: 'commitPage',
      });
      const approveRequest = authRequest(url, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          uuid: currentUserKey,
        }),
      });
      const response = await fetch(approveRequest);
      if (!response.ok) {
        throw new Error('An error occured approving the commit');
      }

      setApproveLoading(false);
      setApproved(true);
      if (currentUser) {
        dispatchCommitUpdate({ type: 'approve', agent: currentUser });
      }
      // response comes back empty so there is nothing to return
      return true;
    } catch (e) {
      setApproveLoading(false);
      return false;
    }
  };

  // unapproves a commit, returns true if successful and false otherwise
  const unapproveCommit = async () => {
    const url = urls.api.v20.approve(fullSlug, hash);
    setApproveLoading(true);
    try {
      publishUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'approveButton',
        source: 'commitPage',
      });
      const unapproveRequest = authRequest(url, {
        method: 'DELETE',
        headers: jsonHeaders,
      });

      const response = await fetch(unapproveRequest);
      if (!response.ok) {
        throw new Error('An error occured unapproving the commit');
      }

      setApproveLoading(false);
      setApproved(false);
      if (currentUser) {
        dispatchCommitUpdate({ type: 'unapprove', agent: currentUser });
      }
      // response comes back empty so there is nothing to return
      return true;
    } catch (e) {
      setApproveLoading(false);
      return false;
    }
  };

  return {
    approveCommit,
    unapproveCommit,
    isApproved,
    isApproveLoading,
  };
}
